import React, { useEffect, useMemo, useState } from "react";
import { X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../../utils/httpRequest";
import { toast } from "react-toastify";
import CategoryFormFields from "./CategoryFormFields";

export default function EditCategoryModal({
  isOpen,
  onClose,
  token,
  category, // شیء دسته انتخاب‌شده برای ویرایش
  allCategories = [], // برای انتخاب والد
  onUpdated, // callback بعد از آپدیت موفق
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const [form, setForm] = useState({
    title: "",
    description: "",
    parent: "",
    slug: "",
    icon: "",
    hasSubscription: false,
  });
  const [saving, setSaving] = useState(false);

  // همگام‌سازی فرم با دستهٔ انتخاب‌شده
  useEffect(() => {
    if (!category) return;
    setForm({
      title: category.title || "",
      description: category.description || "",
      parent: category.parent || "",
      slug: category.slug || "",
      icon: category.icon || "",
      hasSubscription: !!category.hasSubscription,
    });
  }, [category]);

  // جلوگیری از انتخاب خودِ دسته به‌عنوان والد
  const parentOptions = useMemo(
    () => allCategories.filter((c) => c._id !== category?._id),
    [allCategories, category]
  );

  const selectedParent = useMemo(
    () => parentOptions.find((c) => c._id === form.parent),
    [parentOptions, form.parent]
  );
  const willBeLevel = selectedParent ? (selectedParent.level || 1) + 1 : 1;
  const canToggleHasSubscription = willBeLevel === 3;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!category?._id) return;

    // ولیدیشن سبک کلاینت
    if (!form.title?.trim()) {
      toast.error(t("org_categories.toasts.title_required"));
      return;
    }
    if (!form.icon) {
      toast.error(t("org_categories.toasts.icon_required"));
      return;
    }
    if (form.parent === category._id) {
      toast.error(t("org_categories.toasts.self_parent"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || undefined,
        parent: form.parent || undefined,
        slug: form.slug || undefined,
        icon: form.icon || undefined,
        hasSubscription: canToggleHasSubscription
          ? form.hasSubscription
          : false,
      };

      // امضای httpRequest(method, url, data, headers)
      const res = await httpRequest(
        "PUT",
        `/category/${category._id}`,
        payload,
        { "x-access-token": token }
      );

      if (res?.data?._id || res?.status === 200) {
        toast.success(t("org_categories.toasts.updated"));
        onUpdated?.();
        onClose?.();
      } else {
        toast.error(res?.message || t("common.unknown_error"));
      }
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !category) return null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl rounded-2xl shadow-xl p-4 md:p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            {t("org_categories.edit_category")}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <CategoryFormFields
            form={form}
            setForm={setForm}
            allCategories={parentOptions}
            willBeLevel={willBeLevel}
            canToggleHasSubscription={canToggleHasSubscription}
          />

          <div
            className={`flex ${
              isRtl ? "justify-start" : "justify-end"
            } gap-3 pt-2`}
          >
            <button
              type="button"
              className="px-4 py-2 rounded-lg border hover:bg-gray-50"
              onClick={onClose}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {saving ? t("common.saving") : t("org_categories.save_changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
